import { ComputedOrImmediate } from '@funkey/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { LocalJSONStorage } from '~/utils/js';

export type LocalJSONStorageState<T, V = T extends LocalJSONStorage<infer U> ? U : T> = {
    /** Whether or not the storage value has been fetched.
     *
     * If `false`: the state will be equal to the initialValue of the store. */
    isMounted: boolean;
    state: V;
    setState: React.Dispatch<React.SetStateAction<V>>;
    /** NOTE: only use the mutating functions on the store if you want to prevent rerenders.
     * Else you should use `setState()` */
    store: LocalJSONStorage<V>;
};

/** @example
 * ```typescript
 * // Some store declared elsewhere
 * declare const MY_STORE: LocalJSONStorage<number>();
 *
 * const Component: React.FC = props => {
 *    const storage = useLocalJSONStorage(MY_STORE);
 *
 *    // ...
 * }
 * ```
 *  */
export function useLocalStorageState<T>(storage: LocalJSONStorage<T>): LocalJSONStorageState<T>;
/** @example ```typescript
 * const storage = useLocalJSONStorage('participants', 20, 'sessionStorage');
 * ```
 *  */
export function useLocalStorageState<T>(
    key: string,
    initialValue: T,
    storageType: LocalJSONStorage.StorageType,
): LocalJSONStorageState<T>;
export function useLocalStorageState<T>(...args: unknown[]): unknown {
    // eslint-disable-next-line -- `args` is a rest parameter, so the dependency array below is
    // rebuilt every render and cannot be analysed statically. React tolerates that; the compiler
    // does not, and this hook backs the VAT preference behind every displayed price.
    'use no memo';
    const store = useMemo<LocalJSONStorage<T>>(() => {
        if (args[0] instanceof LocalJSONStorage) return args[0];

        return new LocalJSONStorage<T>(
            ...(args as ConstructorParameters<typeof LocalJSONStorage<T>>),
        );
    }, args);

    const queryKey = useMemo(
        () => ['LocalJSONStorage', store.storageType, store.key] as const,
        [store.key, store.storageType],
    );
    const [hasMounted, setHasMounted] = useState(false);

    const qc = useQueryClient();

    useEffect(() => {
        setHasMounted(true);
    }, []);

    const query = useQuery<T, never>({
        queryKey,
        queryFn(_) {
            return store.getValue();
        },
        enabled: hasMounted,
        refetchOnMount: 'always',
    });

    // Sync storage onChange
    // prettier-ignore
    useEffect(() => store.ChangeEvent_listen(e => {
        if (e.detail.key !== store.key) return;
        if (e.detail.storageType !== store.storageType) return;

        qc.refetchQueries({ queryKey, });
    }), [store, queryKey]);

    return useMemo(() => {
        const state = query.data === undefined ? store.initialValue : query.data;
        return {
            isMounted: hasMounted && !query.isPending,
            store,
            state,
            setState(values: React.SetStateAction<T>) {
                store.setValue(current => ComputedOrImmediate.get(values, current));
            },
        };
    }, [hasMounted, query.isPending, query.data, store]);
}
